import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2, Sparkles, Mic, MicOff, Mail, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Generate a unique session ID for this chat
const generateSessionId = () => {
  const stored = sessionStorage.getItem('chat_session_id');
  if (stored) return stored;
  const newId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('chat_session_id', newId);
  return newId;
};

// Page-specific proactive messages
const getProactiveMessage = (pathname) => {
  const messages = {
    '/fence/vinyl': {
      delay: 25000,
      message: "I see you're looking at vinyl fencing! VEKA and Catalyst offer 25+ colors with lifetime warranties. Would you like help choosing a style or getting a quote?",
      quick: ["See Color Options", "Get a Quote", "Compare to Wood"]
    },
    '/fence/wood': {
      delay: 25000,
      message: "Interested in wood fencing? Our Rona MicroPro pressure-treated lumber is perfect for Alberta winters. Want help calculating materials for your project?",
      quick: ["Calculate Materials", "Get a Quote", "See Styles"]
    },
    '/fence/chain-link': {
      delay: 25000,
      message: "Chain link is great for security and visibility! We have residential and commercial grades. Need help picking the right gauge for your needs?",
      quick: ["Residential vs Commercial", "Get a Quote", "Add Privacy Slats"]
    },
    '/fence/ornamental': {
      delay: 25000,
      message: "Ornamental fencing adds elegance to any property! We carry Direct Fencing Supply and Atlas hardware. Looking for a specific style?",
      quick: ["View Styles", "Get a Quote", "Gate Hardware"]
    },
    '/fence/calculator': {
      delay: 20000,
      message: "Need help with your fence calculation? I can explain material requirements or help you get an accurate quote from our team!",
      quick: ["Help with Calculator", "Get Expert Quote", "Delivery Info"]
    },
    '/fence/contractors': {
      delay: 15000,
      message: "Interested in a contractor account? We offer volume discounts, net-30 terms, and priority support. I can help you apply!",
      quick: ["Apply Now", "Volume Discounts", "Account Benefits"]
    },
    '/fence': {
      delay: 30000,
      message: "Welcome to Central Alberta's fence supply experts! With 250+ products and 15+ years experience, we've got what you need. How can I help?",
      quick: ["Browse Products", "Get a Quote", "Contractor Account"]
    },
    '/ps/landscaping': {
      delay: 25000,
      message: "Looking to transform your outdoor space? Our landscaping team creates beautiful, functional designs for Central Alberta properties. Want to discuss your vision?",
      quick: ["Get a Quote", "See Our Work", "Design Consultation"]
    },
    '/ps/snow-removal': {
      delay: 20000,
      message: "Winter is coming! Our snow removal services keep your property safe and accessible. We offer seasonal contracts for worry-free winters.",
      quick: ["Get Seasonal Quote", "Emergency Service", "Commercial Contracts"]
    },
    '/ps/maintenance': {
      delay: 25000,
      message: "Regular property maintenance keeps your investment looking great. We offer one-time and recurring services for homes and businesses.",
      quick: ["Get a Quote", "Recurring Services", "One-Time Cleanup"]
    },
    '/ps': {
      delay: 30000,
      message: "Welcome to CAF Property Services! We provide landscaping, snow removal, and property maintenance across Central Alberta. How can we help you today?",
      quick: ["Landscaping", "Snow Removal", "Get a Quote"]
    },
    '/': {
      delay: 45000,
      message: "Hi there! I'm the CAF Assistant. I can help with both fencing materials AND property services like landscaping and snow removal. What are you looking for today?",
      quick: ["Fencing Materials", "Property Services", "Get a Quote"]
    }
  };

  // Find matching page or return default
  for (const [path, config] of Object.entries(messages)) {
    if (pathname.startsWith(path) && path !== '/') {
      return config;
    }
  }
  
  // Default for homepage or unmatched pages
  return pathname === '/' ? messages['/'] : null;
};

const ChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showProactive, setShowProactive] = useState(false);
  const [proactiveMessage, setProactiveMessage] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Context-aware colors based on route
  const isPropertyServices = location.pathname.startsWith('/ps');
  const primaryColor = isPropertyServices ? '#456CAC' : '#456CAC';
  const primaryColorDark = isPropertyServices ? '#3A5A91' : '#3A5A91';
  const primaryColorLight = isPropertyServices ? '#D1FAE5' : '#F0F4FA';
  
  // Hide ChatWidget on the AI Configurator page (it has its own chat)
  const isConfiguratorPage = location.pathname === '/fence/configurator';
  
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm the CAF Assistant. I can help with fencing materials, property services like landscaping and snow removal, get you a quote, or book a consultation. How can I help you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const proactiveTimerRef = useRef(null);
  
  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  
  // Email transcript state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-CA';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputValue(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        setIsListening(false);
      };
    }
  }, []);

  // Toggle voice input
  const toggleVoiceInput = () => {
    if (!speechSupported || !recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Send email transcript
  const sendTranscript = async () => {
    if (!emailAddress.trim() || emailSending) return;
    
    setEmailSending(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: emailAddress.trim(),
          messages: messages
        })
      });
      
      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setEmailAddress('');
        }, 2000);
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      alert('Failed to send transcript. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Proactive message trigger based on page
  useEffect(() => {
    // Clear any existing timer
    if (proactiveTimerRef.current) {
      clearTimeout(proactiveTimerRef.current);
    }

    // Don't show proactive if user has already interacted or chat is open
    if (hasInteracted || isOpen) {
      setShowProactive(false);
      return;
    }

    const pageConfig = getProactiveMessage(location.pathname);
    if (pageConfig) {
      proactiveTimerRef.current = setTimeout(() => {
        setProactiveMessage(pageConfig);
        setShowProactive(true);
      }, pageConfig.delay);
    }

    return () => {
      if (proactiveTimerRef.current) {
        clearTimeout(proactiveTimerRef.current);
      }
    };
  }, [location.pathname, hasInteracted, isOpen]);

  // Handle proactive message click
  const handleProactiveOpen = () => {
    setHasInteracted(true);
    setShowProactive(false);
    setIsOpen(true);
    
    // Add the proactive message as a context-aware greeting
    if (proactiveMessage) {
      setMessages([{
        role: 'assistant',
        content: proactiveMessage.message
      }]);
    }
  };

  // Dismiss proactive without opening
  const dismissProactive = (e) => {
    e.stopPropagation();
    setShowProactive(false);
    setHasInteracted(true);
  };

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || inputValue.trim();
    if (!textToSend || isLoading) return;

    setHasInteracted(true);
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: textToSend }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: textToSend
        })
      });

      if (!response.ok) throw new Error('Chat request failed');

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again or call us at (403) 986-5484." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Dynamic quick actions based on page context
  const getQuickActions = () => {
    if (proactiveMessage?.quick && messages.length <= 2) {
      return proactiveMessage.quick.map(label => ({
        label,
        message: label === "Apply Now" ? "I'd like to apply for a contractor account" :
                 label === "Get a Quote" ? "I'd like to get a quote" :
                 label === "Calculate Materials" ? "Help me calculate materials for my fence project" :
                 label === "See Color Options" ? "What vinyl fence colors do you offer?" :
                 label === "Contractor Pricing" ? "Tell me about contractor volume discounts" :
                 label === "Fencing Materials" ? "I'm looking for fence materials" :
                 label === "Property Services" ? "I need help with property services" :
                 label === "Landscaping" ? "Tell me about your landscaping services" :
                 label === "Snow Removal" ? "I need snow removal services" :
                 label === "Get Seasonal Quote" ? "I'd like a quote for seasonal snow removal" :
                 label === "Design Consultation" ? "I'd like to schedule a landscaping design consultation" :
                 `Tell me about ${label.toLowerCase()}`
      }));
    }
    
    // Default actions - now includes property services
    const pathname = window.location.pathname;
    if (pathname.startsWith('/ps')) {
      return [
        { label: "Get a Quote", message: "I'd like to get a quote for property services" },
        { label: "Book Consultation", message: "I'd like to book a consultation appointment" },
        { label: "Service Info", message: "What property services do you offer?" },
      ];
    }
    
    return [
      { label: "Get a Quote", message: "I'd like to get a quote for fence materials" },
      { label: "Book Consultation", message: "I'd like to book a consultation appointment" },
      { label: "Product Info", message: "What types of fencing do you sell?" },
    ];
  };

  // Closed state with proactive popup
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        {/* Proactive message popup */}
        {showProactive && proactiveMessage && (
          <div 
            className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in-up cursor-pointer"
            onClick={handleProactiveOpen}
          >
            <div className="px-4 py-2 flex items-center justify-between" style={{ background: `linear-gradient(to right, ${primaryColor}, ${primaryColorDark})` }}>
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">CAF Assistant</span>
              </div>
              <button 
                onClick={dismissProactive}
                className="text-white/80 hover:text-white p-1"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-700 leading-relaxed">{proactiveMessage.message}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {proactiveMessage.quick?.slice(0, 2).map((action, idx) => (
                  <span 
                    key={idx}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ backgroundColor: primaryColorLight, color: primaryColor }}
                  >
                    {action}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Chat trigger button */}
        <button
          onClick={() => { setIsOpen(true); setHasInteracted(true); setShowProactive(false); }}
          className={`text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group ${
            showProactive ? 'animate-bounce-gentle' : ''
          }`}
          style={{ backgroundColor: primaryColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = primaryColorDark}
          onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
          aria-label="Open chat"
          data-testid="chat-widget-trigger"
        >
          <MessageCircle className="w-6 h-6" />
          {showProactive && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
              1
            </span>
          )}
          {!showProactive && (
            <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-slate-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Chat with us!
            </span>
          )}
        </button>
      </div>
    );
  }

  // Hide ChatWidget on the AI Configurator page (it has its own chat)
  if (isConfiguratorPage) {
    return null;
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[32rem]'
      }`}
      data-testid="chat-widget"
    >
      {/* Header */}
      <div 
        className="text-white p-4 flex items-center justify-between cursor-pointer"
        style={{ background: `linear-gradient(to right, ${primaryColor}, ${primaryColorDark})` }}
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">CAF Assistant</h3>
            <p className="text-xs" style={{ color: '#E8EEF7' }}>
              {isLoading ? 'Typing...' : 'Online'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                  style={msg.role === 'user' ? { backgroundColor: '#456CAC' } : {}}
                >
                  {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div 
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'text-white rounded-tr-md'
                      : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-tl-md'
                  }`}
                  style={msg.role === 'user' ? { backgroundColor: '#456CAC' } : {}}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-slate-600" />
                </div>
                <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (show only at start or after proactive) */}
          {messages.length <= 2 && !isLoading && (
            <div className="px-4 py-2 border-t border-slate-100 bg-white">
              <div className="flex flex-wrap gap-2">
                {getQuickActions().map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(action.message)}
                    className="text-xs px-3 py-1.5 rounded-full transition-colors"
                    style={{ backgroundColor: primaryColorLight, color: primaryColor }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#E8EEF7'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#F0F4FA'}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              {/* Voice Input Button */}
              {speechSupported && (
                <button
                  onClick={toggleVoiceInput}
                  className={`p-2.5 rounded-full transition-colors ${
                    isListening 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  aria-label={isListening ? "Stop listening" : "Start voice input"}
                  data-testid="chat-voice-button"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
              
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isListening ? "Listening..." : "Type your message..."}
                className={`flex-1 px-4 py-2.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all ${
                  isListening ? 'ring-2 ring-red-300' : ''
                }`}
                style={{ '--tw-ring-color': '#5A7DBF' }}
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="p-2.5 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = primaryColorDark)}
                onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                aria-label="Send message"
                data-testid="chat-send-button"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            
            {/* Footer with email transcript option */}
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-400">
                Powered by AI • <a href="tel:4039865484" style={{ color: primaryColor }} className="hover:underline">Call (403) 986-5484</a>
              </p>
              {messages.length > 2 && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="text-xs text-slate-500 flex items-center gap-1 transition-colors"
                  style={{ '--hover-color': primaryColor }}
                  onMouseEnter={(e) => e.target.style.color = primaryColor}
                  onMouseLeave={(e) => e.target.style.color = ''}
                  aria-label="Email transcript"
                >
                  <Mail className="w-3 h-3" />
                  <span>Email transcript</span>
                </button>
              )}
            </div>
          </div>

          {/* Email Transcript Modal */}
          {showEmailModal && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10">
              <div className="bg-white rounded-xl p-5 w-full max-w-xs shadow-xl">
                {emailSent ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#E8EEF7' }}>
                      <Check className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <p className="text-sm font-medium text-slate-700">Transcript sent!</p>
                    <p className="text-xs text-slate-500 mt-1">Check your inbox</p>
                  </div>
                ) : (
                  <>
                    <h4 className="font-semibold text-slate-800 mb-3">Email Chat Transcript</h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Get a copy of this conversation sent to your email.
                    </p>
                    <input
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 mb-3"
                      style={{ '--tw-ring-color': '#5A7DBF' }}
                      disabled={emailSending}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setShowEmailModal(false); setEmailAddress(''); }}
                        className="flex-1 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        disabled={emailSending}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={sendTranscript}
                        disabled={!emailAddress.trim() || emailSending}
                        className="flex-1 px-3 py-2 text-sm text-white rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        style={{ backgroundColor: primaryColor }}
                        onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = primaryColorDark)}
                        onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                      >
                        {emailSending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            Send
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatWidget;
